import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import AppLayout from "@/components/layout/AppLayout";
import { AccountSettings } from "./components/AccountSettings";
import { AppearanceSettings } from "./components/AppearanceSettings";
import { BackupSettings } from "./components/BackupSettings";
import { NotificationPreferences } from "./components/NotificationPreferences";
import { ReportsSettings } from "./components/ReportsSettings";
import { useUserRoles } from "@/hooks/useUserRoles";
import ServiceAccountsTab from "./components/ServiceAccountsTab";
import { HourlyCostsSettings } from "./components/HourlyCostsSettings";
import { EquipmentTypesSettings } from "./components/EquipmentTypesSettings";
import { TeamEquipmentTypesSettings } from "./components/TeamEquipmentTypesSettings";
import { GuardEquipmentAssignmentTab } from "./components/GuardEquipmentAssignmentTab";
import { TeamEquipmentAssignmentTab } from "./components/TeamEquipmentAssignmentTab";
import { UserManagement } from "@/components/settings/UserManagement";
import { ContributorManagement } from "@/components/settings/ContributorManagement";
import { ShiftSwapToggle } from "@/components/settings/ShiftSwapToggle";
import { SupplierManagementTab } from "./components/SupplierManagementTab";
import { SupplierEvaluationsTab } from "./components/SupplierEvaluationsTab";
import { CompanyAssetsTab } from "./components/CompanyAssetsTab";
import { ITEquipmentTab } from "./components/ITEquipmentTab";
import { SecurityInventoryTab } from "./components/SecurityInventoryTab";
import { CompanyPoliciesTab } from "./components/CompanyPoliciesTab";
import { Users, PaintBucket, Bell, FileText, Save, Link, Euro, ShieldAlert, Package, Users2, Building2, Settings as SettingsIcon, Shield, ArrowLeftRight, ShieldCheck, Building, Laptop } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/contexts/LanguageContext";
import { useModuleAccess } from "@/hooks/useModuleAccess";
const SettingsPage = () => {
  const { t } = useLanguage();
  const { isEnabled: isModuleEnabled, isLoading: moduleLoading } = useModuleAccess();
  const {
    isAdmin,
    isSuperAdmin
  } = useUserRoles();
  const [activeTab, setActiveTab] = useState("account");

  // Group settings into categories
  const settingsCategories = [{
    id: "general",
    name: t('general'),
    description: t('generalDescription'),
    color: "from-blue-500 to-purple-600",
    tabs: [{
      id: "account",
      name: t('account'),
      icon: <Users className="h-4 w-4 mr-2" />,
      component: <AccountSettings />,
      description: t('manageYourProfile')
    }, {
      id: "appearance",
      name: t('appearance'),
      icon: <PaintBucket className="h-4 w-4 mr-2" />,
      component: <AppearanceSettings />,
      description: t('customizeInterface')
    }, {
      id: "notifications",
      name: t('notifications'),
      icon: <Bell className="h-4 w-4 mr-2" />,
      component: <NotificationPreferences />,
      description: t('controlYourAlerts')
    }]
  }, {
    id: "data",
    name: t('dataManagement'),
    description: t('dataManagementDescription'),
    color: "from-green-500 to-teal-600",
    tabs: [{
      id: "reports",
      name: t('reports'),
      icon: <FileText className="h-4 w-4 mr-2" />,
      component: <ReportsSettings />,
      description: t('configureReportSettings')
    }, {
      id: "backup",
      name: t('backup'),
      icon: <Save className="h-4 w-4 mr-2" />,
      component: <BackupSettings />,
      description: t('manageDataBackups')
    }, ...(isAdmin ? [{
      id: "integrations",
      name: t('integrations'),
      icon: <Link className="h-4 w-4 mr-2" />,
      component: <ServiceAccountsTab />,
      description: t('externalConnections')
    }] : [])]
  }, ...(isAdmin && (isModuleEnabled("equipment_management") || isModuleEnabled("supplier_evaluations")) ? [{
    id: "equipment",
    name: t('equipment'),
    description: t('equipmentDescription'),
    color: "from-orange-500 to-red-600",
    tabs: [{
      id: "equipment-types",
      name: t('guardEquipmentTypes'),
      icon: <ShieldAlert className="h-4 w-4 mr-2" />,
      component: <EquipmentTypesSettings />,
      description: t('guardEquipmentCatalog')
    }, {
      id: "team-equipment-types",
      name: t('teamEquipmentTypes'),
      icon: <Building2 className="h-4 w-4 mr-2" />,
      component: <TeamEquipmentTypesSettings />,
      description: t('teamEquipmentCatalog')
    }, {
      id: "suppliers",
      name: t('suppliers'),
      icon: <Package className="h-4 w-4 mr-2" />,
      component: <SupplierManagementTab />,
      description: t('supplierManagement')
    }, ...(isModuleEnabled("supplier_evaluations") ? [{
      id: "supplier-evaluations",
      name: t('supplierEvaluations'),
      icon: <ShieldCheck className="h-4 w-4 mr-2" />,
      component: <SupplierEvaluationsTab />,
      description: "ISO 9001"
    }] : []), ...(isModuleEnabled("equipment_management") ? [{
      id: "company-assets",
      name: t('companyAssets'),
      icon: <Building className="h-4 w-4 mr-2" />,
      component: <CompanyAssetsTab />,
      description: t('assetManagement')
    }, {
      id: "security-inventory",
      name: t('securityInventory'),
      icon: <Shield className="h-4 w-4 mr-2" />,
      component: <SecurityInventoryTab />,
      description: t('guardEquipmentList')
    }, {
      id: "it-equipment",
      name: t('itEquipment'),
      icon: <Laptop className="h-4 w-4 mr-2" />,
      component: <ITEquipmentTab />,
      description: t('itEquipmentManagement')
    }, {
      id: "assign-guard-equipment",
      name: t('assignEquipmentToGuards'),
      icon: <Users className="h-4 w-4 mr-2" />,
      component: <GuardEquipmentAssignmentTab />,
      description: t('guardAssignments')
    }, {
      id: "assign-team-equipment",
      name: t('assignEquipmentToTeams'),
      icon: <Users2 className="h-4 w-4 mr-2" />,
      component: <TeamEquipmentAssignmentTab />,
      description: t('teamAssignments')
    }] : [])
  ]}] : []), ...(isAdmin && isModuleEnabled("finance") ? [{
    id: "finance",
    name: t('finance'),
    description: t('financeDescription'),
    color: "from-emerald-500 to-cyan-600",
    tabs: [{
      id: "hourly-costs",
      name: t('hourlyCosts'),
      icon: <Euro className="h-4 w-4 mr-2" />,
      component: <HourlyCostsSettings />,
      description: t('configureHourlyRates')
    }]
  }] : []), {
    id: "scheduling",
    name: t('scheduling'),
    description: t('schedulingDescription'),
    color: "from-violet-500 to-purple-600",
    tabs: [{
      id: "shift-swaps",
      name: t('shiftSwaps'),
      icon: <ArrowLeftRight className="h-4 w-4 mr-2" />,
      component: <ShiftSwapToggle />,
      description: t('enableShiftSwapRequests')
    }]
  }, ...(isAdmin && isModuleEnabled("ethics_compliance") ? [{
    id: "compliance",
    name: t('ethicsCompliance'),
    description: t('companyPolicies'),
    color: "from-indigo-500 to-blue-600",
    tabs: [{
      id: "company-policies",
      name: t('companyPolicies'),
      icon: <Shield className="h-4 w-4 mr-2" />,
      component: <CompanyPoliciesTab />,
      description: t('uploadPolicy')
    }]
  }] : []), ...((isAdmin || isSuperAdmin) ? [{
    id: "system",
    name: t('systemAdministration'),
    description: t('systemAdministrationDescription'),
    color: "from-red-500 to-pink-600",
    tabs: [
      ...(isAdmin && !isSuperAdmin ? [{
        id: "contributors",
        name: "Contributors",
        icon: <Users2 className="h-4 w-4 mr-2" />,
        component: <ContributorManagement />,
        description: "Manage contributor admins who share your permissions"
      }] : []),
      ...(isSuperAdmin ? [{
        id: "user-management",
        name: t('userManagement'),
        icon: <Shield className="h-4 w-4 mr-2" />,
        component: <UserManagement />,
        description: t('manageUsersAndRoles')
      }] : [])
    ]
  }] : [])];
  const getCurrentCategoryTitle = () => {
    for (const category of settingsCategories) {
      const foundTab = category.tabs.find(tab => tab.id === activeTab);
      if (foundTab) {
        return {
          categoryName: category.name,
          tabName: foundTab.name,
          description: foundTab.description
        };
      }
    }
    return {
      categoryName: "Settings",
      tabName: "General",
      description: "Manage your preferences"
    };
  };
  const currentInfo = getCurrentCategoryTitle();
  return <AppLayout>
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 relative overflow-hidden">
        {/* Enhanced background patterns */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/3 via-transparent to-blue-500/3 pointer-events-none"></div>
        <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-radial from-blue-500/8 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-radial from-primary/8 to-transparent rounded-full blur-3xl pointer-events-none animate-pulse" style={{
        animationDelay: '2s'
      }}></div>
        
        <div className="relative z-10 container py-6 md:py-10">
          {/* Header Section */}
          <div className="mb-12 text-center relative animate-fade-in">
            <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-blue-500/10 to-purple-500/10 rounded-3xl blur-3xl"></div>
            <div className="relative">
              <div className="flex items-center justify-center mb-6">
                <div className="p-4 bg-gradient-to-br from-primary to-primary/80 rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                  <SettingsIcon className="h-10 w-10 text-white" />
                </div>
                <div className="ml-4 flex space-x-1">
                  
                  
                  
                </div>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-foreground via-primary to-blue-600 bg-clip-text text-transparent mb-4 tracking-tight">
                {t('settingsTitle')}
              </h1>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                {t('personalizeWorkspace')}
              </p>
              <div className="mt-6 flex justify-center">
                <div className="h-1 w-20 bg-gradient-to-r from-primary to-blue-500 rounded-full"></div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8 animate-fade-in">
            {/* Enhanced Settings Navigation */}
            <div className="space-y-6">
              <Card className="overflow-hidden border-0 shadow-2xl bg-gradient-to-br from-card/95 to-muted/30 backdrop-blur-lg hover:shadow-3xl transition-all duration-500">
                <CardHeader className="pb-4 bg-gradient-to-r from-primary/5 to-blue-500/5">
                  <CardTitle className="text-sm font-semibold text-muted-foreground uppercase tracking-wider flex items-center">
                    <div className="w-2 h-2 bg-gradient-to-r from-primary to-blue-500 rounded-full mr-2 animate-pulse"></div>
                    {t('navigation')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Accordion type="multiple" defaultValue={["general", "system"]} className="w-full">
                    {settingsCategories.filter(cat => cat.tabs.length > 0).map((category, index) => <AccordionItem key={category.id} value={category.id} className="border-0">
                        <AccordionTrigger className="px-6 py-5 hover:bg-gradient-to-r hover:from-muted/50 hover:to-muted/30 transition-all duration-300 [&[data-state=open]]:bg-gradient-to-r [&[data-state=open]]:from-primary/10 [&[data-state=open]]:to-blue-500/10 group" style={{
                      animationDelay: `${index * 100}ms`
                    }}>
                          <div className="flex items-center space-x-4">
                            <div className={`w-4 h-4 rounded-full bg-gradient-to-r ${category.color} shadow-lg group-hover:scale-110 transition-transform duration-200`}></div>
                            <div className="text-left">
                              <div className="font-bold text-base group-hover:text-primary transition-colors duration-200">{category.name}</div>
                              <div className="text-sm text-muted-foreground group-hover:text-muted-foreground/80">{category.description}</div>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pb-0">
                          <div className="space-y-2 pl-6 pr-6 pb-6">
                            {category.tabs.map((tab, tabIndex) => <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-start justify-between w-full text-left rounded-2xl px-5 py-4 text-sm transition-all duration-300 group relative overflow-hidden ${activeTab === tab.id ? `bg-gradient-to-r ${category.color} text-white shadow-xl scale-[1.03] ring-2 ring-white/20` : "hover:bg-muted/60 hover:scale-[1.02] hover:shadow-md"}`} style={{
                          animationDelay: `${tabIndex * 50}ms`
                        }}>
                                {activeTab === tab.id && <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent pointer-events-none"></div>}
                                <div className="flex items-center min-w-0 flex-1 relative z-10">
                                  <div className={`${activeTab === tab.id ? 'text-white' : 'text-muted-foreground'} group-hover:scale-125 transition-all duration-300 mr-1`}>
                                    {tab.icon}
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className={`font-semibold text-base ${activeTab === tab.id ? 'text-white' : 'text-foreground'} group-hover:text-primary transition-colors duration-200`}>
                                      {tab.name}
                                    </div>
                                    <div className={`text-xs ${activeTab === tab.id ? 'text-white/90' : 'text-muted-foreground'} truncate leading-relaxed`}>
                                      {tab.description}
                                    </div>
                                  </div>
                                </div>
                                {activeTab === tab.id && <div className="flex items-center space-x-1 ml-3 flex-shrink-0 relative z-10">
                                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                                    <div className="w-1 h-1 bg-white/70 rounded-full animate-pulse" style={{
                              animationDelay: '0.5s'
                            }}></div>
                                  </div>}
                              </button>)}
                          </div>
                        </AccordionContent>
                      </AccordionItem>)}
                  </Accordion>
                </CardContent>
              </Card>

              {/* Admin Badge */}
              {isAdmin && <Card className="border-0 bg-gradient-to-br from-amber-50/80 to-orange-100/80 dark:from-amber-900/30 dark:to-orange-900/30 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 animate-fade-in">
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-3">
                      <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 shadow-md hover:shadow-lg transition-shadow duration-200 px-3 py-1">
                        <ShieldAlert className="w-4 h-4 mr-2" />
                        {t('adminAccess')}
                      </Badge>
                      <div className="w-3 h-3 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-pulse shadow-sm"></div>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {t('adminPrivilegesMessage')}
                    </p>
                  </CardContent>
                </Card>}
            </div>
            
            {/* Enhanced Settings Content */}
            <div className="space-y-8">
              {/* Current Section Header */}
              <Card className="border-0 bg-gradient-to-r from-primary/5 via-card to-blue-500/5 shadow-2xl backdrop-blur-sm hover:shadow-3xl transition-all duration-500">
                <CardHeader className="pb-6 relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-primary/10 to-blue-500/10 rounded-full blur-2xl"></div>
                  <div className="relative">
                    <div className="flex items-center space-x-3 mb-3">
                      <Badge variant="outline" className="text-sm font-medium px-3 py-1 bg-gradient-to-r from-primary/10 to-blue-500/10 border-primary/20 text-primary">
                        {currentInfo.categoryName}
                      </Badge>
                      <div className="h-1 w-8 bg-gradient-to-r from-primary to-blue-500 rounded-full"></div>
                    </div>
                    <CardTitle className="text-3xl font-bold bg-gradient-to-r from-foreground via-primary to-blue-600 bg-clip-text text-transparent mb-2 tracking-tight">
                      {currentInfo.tabName}
                    </CardTitle>
                    <CardDescription className="text-lg text-muted-foreground leading-relaxed">
                      {currentInfo.description}
                    </CardDescription>
                  </div>
                </CardHeader>
              </Card>

              {/* Settings Content */}
              <Card className="border-0 shadow-2xl bg-card/95 backdrop-blur-lg hover:shadow-3xl transition-all duration-500 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-blue-500/5 pointer-events-none"></div>
                <CardContent className="p-10 relative">
                  <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="hidden">
                      {settingsCategories.flatMap(category => category.tabs.map(tab => <TabsTrigger key={tab.id} value={tab.id}>
                            {tab.name}
                          </TabsTrigger>))}
                    </TabsList>
                    
                    {settingsCategories.flatMap(category => category.tabs.map(tab => <TabsContent key={tab.id} value={tab.id} className="mt-0">
                          <div className="animate-fade-in">
                            {tab.component}
                          </div>
                        </TabsContent>))}
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>;
};
export default SettingsPage;